import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    vec3,
} from 'gl-matrix';
import { SliceData } from './SliceData';
import { SliceSpacing, SliceSpacingType } from './SliceSpacing';

/**
 * {@link SliceSource} acts as a common abstraction to all datamodels representing 3D volume made up of parallel planes.\
 * It provides an Array for storing the {@link SliceData data} of each individual rectangle/slice.\
 * {@link SliceSource} also maintains the correct order of those {@link slices Slices}.
 */
export abstract class SliceSource<T extends SliceData> {
    /**
     * Threshold that determines the maximum variation between two consecutive {@link SliceData slices} to be considered equidistant.
     */
    public static readonly SLICE_SPACING_THRESHOLD = 0.1;

    /**
     * Collection of {@link SliceData slices} that make up this {@link SliceSource}-object.
     */
    protected slices: T[] = [];

    /**
     * The {@link SliceSpacing} between consecutive {@link slices}.\
     * It may be of type:
     * - {@link SliceSpacingType.CONSTANT} if the {@link SliceSpacing} between every two (2) slices is equal.
     */
    protected readonly _sliceSpacing: SliceSpacing;

    public constructor(resZ: number) {
        this._sliceSpacing = new SliceSpacing(SliceSpacingType.CONSTANT, resZ);
    }

    /**
     * Method to get the number of {@link SliceData slices} available within this {@link SliceSource}.
     */
    public get numberOfSlices(): number {
        return this.slices.length;
    }

    /**
     * {@link ReadonlyMat3 Rotation} from ImageSpace to PatientSpace.
     */
    public abstract get rotation(): ReadonlyMat3;

    /**
     * {@link ReadonlyMat3 Rotation} from PatientSpace to ImageSpace.\
     * This is the {@link mat3.transpose Transpose} of {@link rotation}.
     */
    public get invRotation(): ReadonlyMat3 {
        return mat3.transpose(mat3.create(), this.rotation);
    }

    /**
     * PatientSpace {@link ReadonlyVec3 coordinates} of the centerPoint of this {@link SliceSource}.
     */
    public abstract get center(): ReadonlyVec3;

    /**
     * Width of this {@link SliceSource} in millimeters.
     */
    public abstract get widthMm(): number;

    /**
     * Height of this {@link SliceSource} in millimeters.
     */
    public abstract get heightMm(): number;

    /**
     * Method that will find the {@link _sliceSpacing sliceSpacing} of the slices within this {@link SliceSource}.\
     * It finds the slice spacing between every two consecutive {@link slices} and stores all the unique spacings in ascending order.\
     * After that it loops through the spacings to see if all the spacings are multiples of the smallest spacing.\
     * There are four possible outcomes:
     * 1. {@link numberOfSlices} is <= 1. Result is undefined.
     * 2. {@link _sliceSpacing SliceSpacing} is constant. All the {@link slices} are equidistant.\
     * Result is that {@link _sliceSpacing SliceSpacing}.
     * 3. All the {@link _sliceSpacing SliceSpacings} are multiples of the smallest spacing.\
     * Meaning that the dataset has missing slices.\
     * Result is the smallest {@link _sliceSpacing SliceSpacing}.
     * 4. {@link _sliceSpacing SliceSpacing} is varying. Meaning that the spacing between any two consecutive slices is not multiple of the smallest spacing.\
     * Result is undefined.\
     * TODO: This could be optimized, so only newly added slice and it's neighbors are considered.
     */
    public get sliceSpacing(): SliceSpacing {
        return this._sliceSpacing;
    }

    /**
     * Method to add a {@link SliceData slice} to this {@link SliceSource}.\
     * This clears the {@link _sliceSpacing sliceSpacing} and adds the {@link slice} to correct position.
     * @param slice {@link SliceData Slice} to be added to this {@link SliceSource}.
     * @returns The index at which the slice was inserted in. -1 if it wasn't inserted.\
     * TODO: Could optimize the {@link _sliceSpacing sliceSpacing} check here.
     */
    public addSlice(slice: T) {
        this.slices.push(slice);
        this.slices = this.slices.sort((a: T, b: T): number => {
            return a.normalPosition - b.normalPosition;
        });

        let idx = 0;
        for (const s of this.slices) {
            s.setSliceIndex(idx);
            idx++;
        }
    }

    protected clearSlices() {
        this.slices = [];
    }

    /**
     * Method to get the {@link T slice} at given {@link sliceIndex}.
     * @param sliceIndex {@link SliceData.sliceIndex Index} of the {@link T slice} whose {@link SliceData.normalPosition normalPosition} is to be retrieved.
     * @returns Either {@link T slice} at {@link sliceIndex},\
     * or undefined if none of the {@link T slices} has that index.
     */
    public sliceIndexToSlice(sliceIndex: number): T | undefined {
        return this.slices.find((slice: T) => sliceIndex === slice.sliceIndex);
    }

    /**
     * Method to get the actual slice index for specified normalPosition.\
     * The returned slice index value may be decimal number, if none of the slices has the specified normal position.\
     * It may also be negative, if normalPosition is below the first slice.\
     * Or > (numberOfSlices - 1), if normalPosition is above the last slice.
     * @param normalPosition
     * @returns
     */
    public sliceIndexToNormalPosition(sliceIndex: number): number {
        const bottomSlice = this.slices[0];
        return (
            bottomSlice.normalPosition + sliceIndex * this.sliceSpacing.spacing
        );
    }

    public abstract patientCoordsToImageIndices(
        patientCoords: ReadonlyVec3
    ): ReadonlyVec2;

    /**
     * Method to convert {@link ReadonlyVec3 point} from ImageSpace to PatientSpace.
     * @param image ImageSpace {@link ReadonlyVec3 coordinates} of the point that is to be converted to PatientSpace.
     */
    public toPatientSpace(image: ReadonlyVec3): vec3 {
        const planar = vec3.fromValues(
            image[0] * this.widthMm * 0.5,
            image[1] * this.heightMm * 0.5,
            0
        );
        const planarCenter = vec3.transformMat3(
            vec3.create(),
            this.center,
            this.invRotation
        );
        planarCenter[2] = 0;
        vec3.transformMat3(planarCenter, planarCenter, this.rotation);
        const normalPosition = vec3.scale(
            vec3.create(),
            vec3.fromValues(
                this.rotation[6],
                this.rotation[7],
                this.rotation[8]
            ),
            this.sliceIndexToNormalPosition(image[2])
        );
        const rotated = vec3.transformMat3(
            vec3.create(),
            planar,
            this.rotation
        );
        vec3.add(rotated, rotated, planarCenter);
        vec3.add(rotated, rotated, normalPosition);
        return rotated;
    }

    public abstract generateVertex(
        x: -1 | 1,
        y: -1 | 1,
        normalPosition: number
    ): vec3;

    public abstract generateVertex(
        x: -1 | 1,
        y: -1 | 1,
        normalPosition: number
    ): vec3;

    [Symbol.iterator](): Iterator<T, T, undefined> {
        let index = 0;
        return {
            next: (): IteratorResult<T, T> => {
                if (index < this.slices.length) {
                    return { value: this.slices[index++], done: false };
                } else {
                    return { done: true } as IteratorResult<T>;
                }
            },
        };
    }
}

/**
 * Interface that may be given to {@link SliceSource} to request updates on changes in {@link SliceData}.
 */
export interface SliceSourceListener {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSlicesRemoved(sliceSource: SliceSource<any>): void;
    /**
     * Method that the {@link SliceSource} will call when a new {@link SliceData slice} is added.
     * @param sliceSource The {@link SliceSource} that triggered the event.
     * @param slice The {@link SliceData slice} that was added.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSliceAdded(sliceSource: SliceSource<any>, slice: SliceData): void;
}
